import React, { useState } from 'react';
import { FiCalendar, FiCheckCircle, FiClock } from 'react-icons/fi';
import PrintedQuestion from './PrintedQuestion';
import HeaderPrinted from './HeaderPrinted';
import IdentificationForm from './IdentificationForm';
import AnswerKey from './AnswerKey';
import { useLocation, useParams } from 'react-router-dom';
import { useQuestionBook } from '@/hooks/useQuestionBook';
import { useEffect } from 'react';
import { t } from 'i18next';
import moment from 'moment';
import { useClient } from 'jsonapi-react';
import { toast } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { ARUCO_MARKERS } from './arucoMarkers';
import api from '@/services/api';
import { Loader } from '@/components/custom/loader';

export function PrintedExam() {
  const { id } = useParams();
  const location = useLocation();
  const mode = location.state?.mode || 'questions';
  const client = useClient();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [course, setCourse] = useState([]);
  const [students, setStudents] = useState([]);
  const [tokensByUser, setTokensByUser] = useState({});
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const { getQuestionBooks, getQuestionBook, questionBook } = useQuestionBook();

  const getQuestions = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`questions?filter[question_book_id]=${parseInt(id)}`);
    if (error) {
      toast.error('Erro ao buscar alternativas');
    } else {
      setQuestions(data);
    }
    setLoading(false);
  };

  const getCourse = async () => {
    setLoading(true);
    const { data } = await client.fetch(`courses/${questionBook?.['course-ids']?.[0]}`);
    setCourse(data);
    setLoading(false);
  };

  const getStudents = async () => {
    const { data, error } = await client.fetch(`/users/question_books/${parseInt(id)}/able_to_answer_sheet`);
    if (error) {
      toast.error('Erro ao buscar alunos');
    } else {
      const studentIds = location.state?.studentIds;
      const filtered = studentIds?.length ? (data || []).filter(student => studentIds.includes(student.id)) : data || [];
      setStudents(filtered);
      getAnswerSheetTokens(filtered);
    }
  };

  // Tokens assinados por aluno (backend) p/ o QR do cartao-resposta. O QR permite
  // o upload em lote rotear cada scan ao aluno certo. Falha aqui nao bloqueia a
  // impressao (so nao imprime o QR).
  const getAnswerSheetTokens = async studentList => {
    const userIds = (studentList || []).map(student => student?.id).filter(Boolean);
    if (!userIds.length) return;
    try {
      const res = await api.post(`question_books/${parseInt(id)}/answer_sheet_tokens`, { user_ids: userIds });
      const map = {};
      (res?.data || []).forEach(item => {
        map[item['user-id']] = item.token;
      });
      setTokensByUser(map);
    } catch (error) {
      // QR indisponivel: cartao imprime sem QR (fallback pro fluxo manual)
    }
  };

  const getCourses = async () => {
    const { data } = await client.fetch('courses/simple');
    setCourses(data || []);
  };

  const getClassrooms = async () => {
    const { data } = await client.fetch('classrooms');
    setClassrooms(data || []);
  };

  useEffect(() => {
    getQuestionBook(parseInt(id));
    getQuestionBooks();
    getQuestions();
    // modo "questions" nao faz loop por aluno -- lista de alunos/tokens de QR/
    // turmas so sao usados no cartao-resposta (modo "answer-sheet").
    if (mode !== 'questions') {
      getStudents();
      getCourses();
      getClassrooms();
    }
  }, []);

  useEffect(() => {
    if (questionBook) {
      getCourse();
    }
  }, [questionBook]);

  // O navegador usa document.title como sugestao de nome de arquivo no
  // "Salvar como PDF" da impressao -- sem isso ele sugere o titulo generico
  // da rota (ex: "Impressão"), igual pros tres modos.
  useEffect(() => {
    if (!questionBook?.title) return undefined;
    const previousTitle = document.title;
    const prefix = { questions: 'Questões', 'answer-sheet': 'Cartão Resposta', full: 'Questões + Cartão Resposta' }[mode] || 'Questões';
    document.title = `${prefix} - ${questionBook.title}`;
    return () => {
      document.title = previousTitle;
    };
  }, [mode, questionBook?.title]);

  const orderingQuestions = questionBook?.['question-book-taxonomy-questions']?.map(item => item?.['question-id'])?.map(id => questions.find(item => item?.id === id));

  const scopedTitles = (studentItemIds = [], bookItemIds = [], list = []) => {
    const scoped = bookItemIds.length ? studentItemIds.filter(itemId => bookItemIds.includes(itemId)) : studentItemIds;
    return list
      .filter(item => scoped.includes(item.id))
      .map(item => item.title)
      .join(', ');
  };

  if (loading) {
    return <Loader />;
  } else if (mode === 'questions') {
    // Uma unica copia da prova (sem loop por aluno, sem cartao-resposta) --
    // evita gerar paginas-da-prova x alunos num unico documento.
    return (
      <div className="printed-exam">
        <HeaderPrinted institution={course?.institution?.name} />

        <div className="printed-exam__info">
          <h2 className="printed-exam__title">{questionBook?.title}</h2>
          <div className="printed-exam__meta">
            <p className="printed-exam__meta-item">
              <FiCalendar className="printed-exam__icon" />
              <span>
                {t('exams.from')}
                <strong> {moment(questionBook?.['published-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{moment(questionBook?.['published-at']).format('HH:mm')}</strong> {t('exams.to')}{' '}
                <strong> {moment(questionBook?.['finished-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{moment(questionBook?.['finished-at']).format('HH:mm')}</strong>
              </span>
            </p>
            <p className="printed-exam__meta-item">
              <FiClock className="printed-exam__icon" />{' '}
              {questionBook?.['resolution-time'] != '01:00' ? (
                <span dangerouslySetInnerHTML={{ __html: t('exams.hoursDuration', { hours: questionBook?.['resolution-time'] }) }} />
              ) : (
                <span dangerouslySetInnerHTML={{ __html: t('exams.hourDuration', { hours: questionBook?.['resolution-time'] }) }} />
              )}
            </p>
            <p className="printed-exam__meta-item">
              <FiCheckCircle className="printed-exam__icon" /> {questionBook?.['question-count']} {t('exams.questions')}
            </p>
          </div>
          <p className="printed-exam__description">{questionBook?.['description']}</p>
        </div>

        <div className="printed-exam__questions">
          {orderingQuestions?.map((item, index) => (
            <PrintedQuestion
              key={item?.id ?? index}
              question={item}
              index={index}
            />
          ))}
        </div>
      </div>
    );
  } else if (mode === 'full') {
    // Formato original: prova completa + cartao-resposta, com loop por aluno
    // (cabecalho de identificacao do aluno na primeira pagina).
    return (
      <>
        {(students.length ? students : [null]).map((student, bookletIndex) => (
          <div
            className="printed-exam"
            key={student?.id ?? 'blank'}
            style={bookletIndex > 0 ? { pageBreakBefore: 'always' } : undefined}
          >
            <HeaderPrinted institution={course?.institution?.name} />

            <IdentificationForm
              student={student}
              series={scopedTitles(student?.['course-ids'], questionBook?.['course-ids'], courses)}
              classGroup={scopedTitles(student?.['classroom-ids'], questionBook?.['classroom-ids'], classrooms)}
            />

            <div className="printed-exam__info">
              <h2 className="printed-exam__title">{questionBook?.title}</h2>
              <div className="printed-exam__meta">
                <p className="printed-exam__meta-item">
                  <FiCalendar className="printed-exam__icon" />
                  <span>
                    {t('exams.from')}
                    <strong> {moment(questionBook?.['published-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{moment(questionBook?.['published-at']).format('HH:mm')}</strong> {t('exams.to')}{' '}
                    <strong> {moment(questionBook?.['finished-at']).format(t('datesForm.formatDate'))}</strong> {t('exams.at')} <strong>{moment(questionBook?.['finished-at']).format('HH:mm')}</strong>
                  </span>
                </p>
                <p className="printed-exam__meta-item">
                  <FiClock className="printed-exam__icon" />{' '}
                  {questionBook?.['resolution-time'] != '01:00' ? (
                    <span dangerouslySetInnerHTML={{ __html: t('exams.hoursDuration', { hours: questionBook?.['resolution-time'] }) }} />
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: t('exams.hourDuration', { hours: questionBook?.['resolution-time'] }) }} />
                  )}
                </p>
                <p className="printed-exam__meta-item">
                  <FiCheckCircle className="printed-exam__icon" /> {questionBook?.['question-count']} {t('exams.questions')}
                </p>
              </div>
              <p className="printed-exam__description">{questionBook?.['description']}</p>
            </div>

            <div className="printed-exam__questions">
              {orderingQuestions?.map((item, index) => (
                <PrintedQuestion
                  key={item?.id ?? index}
                  question={item}
                  index={index}
                />
              ))}
            </div>

            <div style={{ pageBreakBefore: 'always' }} />

            {/* Cabecalho do cartao-resposta: UM bloco unico -> esquerda info +
                dados do aluno, direita o QR de identificacao. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <HeaderPrinted institution={course?.institution?.name} />
                <IdentificationForm
                  student={student}
                  series={scopedTitles(student?.['course-ids'], questionBook?.['course-ids'], courses)}
                  classGroup={scopedTitles(student?.['classroom-ids'], questionBook?.['classroom-ids'], classrooms)}
                  minimal
                />
              </div>
              {student?.id && tokensByUser[student.id] && (
                <div style={{ textAlign: 'center', flexShrink: 0, paddingLeft: 12, borderLeft: '1px solid #eee' }}>
                  <QRCodeSVG
                    value={tokensByUser[student.id]}
                    size={56}
                    level="M"
                  />
                </div>
              )}
            </div>

            {/* Fiducials ArUco nos 4 cantos: o OMR robusto (robust_omr) detecta e
                corrige perspectiva/rotação, sem depender do "maior retângulo". As
                posições exatas das bolhas são obtidas por calibração (scan em branco),
                então o grid HTML não precisa bater pixel-a-pixel. */}
            <div
              className="printed-exam__answer-sheet"
              style={{ position: 'relative', padding: 52 }}
            >
              {[
                { id: 0, style: { top: 0, left: 0 } },
                { id: 1, style: { top: 0, right: 0 } },
                { id: 2, style: { bottom: 0, right: 0 } },
                { id: 3, style: { bottom: 0, left: 0 } }
              ].map(m => (
                <img
                  key={m.id}
                  src={ARUCO_MARKERS[m.id]}
                  alt=""
                  aria-hidden="true"
                  style={{ position: 'absolute', width: 44, height: 44, imageRendering: 'pixelated', zIndex: 2, ...m.style }}
                />
              ))}

              <div className="answer-key-wrapper">
                {orderingQuestions?.slice(0, 100).map((item, i) => (
                  <AnswerKey
                    question={item}
                    index={i}
                    key={i}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  } else {
    // Um cartao-resposta por aluno (sem as questoes da prova).
    return (
      <>
        {(students.length ? students : [null]).map((student, bookletIndex) => (
          <div
            className="printed-exam"
            key={student?.id ?? 'blank'}
            style={bookletIndex > 0 ? { pageBreakBefore: 'always' } : undefined}
          >
            {/* Cabecalho do cartao-resposta: UM bloco unico -> esquerda info +
                dados do aluno, direita o QR de identificacao. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <HeaderPrinted institution={course?.institution?.name} />
                <IdentificationForm
                  student={student}
                  series={scopedTitles(student?.['course-ids'], questionBook?.['course-ids'], courses)}
                  classGroup={scopedTitles(student?.['classroom-ids'], questionBook?.['classroom-ids'], classrooms)}
                  minimal
                />
              </div>
              {student?.id && tokensByUser[student.id] && (
                <div style={{ textAlign: 'center', flexShrink: 0, paddingLeft: 12, borderLeft: '1px solid #eee' }}>
                  <QRCodeSVG
                    value={tokensByUser[student.id]}
                    size={56}
                    level="M"
                  />
                </div>
              )}
            </div>

            {/* Fiducials ArUco nos 4 cantos: o OMR robusto (robust_omr) detecta e
                corrige perspectiva/rotação, sem depender do "maior retângulo". As
                posições exatas das bolhas são obtidas por calibração (scan em branco),
                então o grid HTML não precisa bater pixel-a-pixel. */}
            <div
              className="printed-exam__answer-sheet"
              style={{ position: 'relative', padding: 52 }}
            >
              {[
                { id: 0, style: { top: 0, left: 0 } },
                { id: 1, style: { top: 0, right: 0 } },
                { id: 2, style: { bottom: 0, right: 0 } },
                { id: 3, style: { bottom: 0, left: 0 } }
              ].map(m => (
                <img
                  key={m.id}
                  src={ARUCO_MARKERS[m.id]}
                  alt=""
                  aria-hidden="true"
                  style={{ position: 'absolute', width: 44, height: 44, imageRendering: 'pixelated', zIndex: 2, ...m.style }}
                />
              ))}

              <div className="answer-key-wrapper">
                {orderingQuestions?.slice(0, 100).map((item, i) => (
                  <AnswerKey
                    question={item}
                    index={i}
                    key={i}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }
}
